import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { siWhatsapp } from "simple-icons";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Check,
  CircleDot,
  Copy,
  Download,
  ExternalLink,
  Inbox,
  Mail,
  MessageSquare,
  Paperclip,
  Phone,
  Trash2,
} from "lucide-react";
import {
  useBot,
  useContact,
  useContacts,
  useContactStats,
  useContactForm,
  useSetContactStatus,
  useDeleteContact,
} from "../hooks/useApi";
import { api } from "../api/client";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { BrandCard, BrandStyles, HandNote, VARIABILI_SEZIONE } from "../components/integrations/brand";
import {
  BOTTONE,
  GRIGIO,
  SCURO,
  IntestazioneCasella,
  Interruttore,
  NessunaScelta,
  Paginazione,
  Ricerca,
  raggruppa,
  schermoLargo,
} from "../components/integrations/casella";
import {
  emailDi,
  gruppoDi,
  iniziali,
  isEmail,
  isTelefono,
  nomeDi,
  paginaLeggibile,
  quandoBreve,
  quandoEsteso,
  telLink,
  telefonoDi,
  whatsappDa,
} from "../components/contacts/recapiti";

const PER_PAGINA = 25;
// Quanto aspettare dopo l'ultima lettera prima di cercare.
const ATTESA_RICERCA = 250;

const isFile = (value) => Boolean(value && typeof value === "object" && !Array.isArray(value) && value.upload_id);

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/**
 * I contatti lasciati in chat, come una casella di posta: a sinistra
 * l'elenco (in arrivo o archiviati, raggruppati per giorno), a destra la
 * scheda di chi si sceglie, con i pulsanti per ricontattarlo. Sul telefono
 * elenco e scheda si alternano. Il contatto aperto sta nell'indirizzo
 * (?contatto=), cosi' il link dell'email di notifica apre proprio lui.
 *
 * Il modulo che li raccoglie si configura nella sua app in Apps & Integrazioni.
 */
export default function ContactsPage() {
  const { id: botId } = useParams();
  const [searchParams] = useSearchParams();
  // Il compositore stava qui (?scheda=modulo): i link vecchi portano alla sua app.
  if (searchParams.get("scheda") === "modulo") {
    return <Navigate to={`/bots/${botId}/integrazioni/modulo-contatti`} replace />;
  }
  // Passando da un bot all'altro la rotta resta la stessa: la chiave rimonta
  // la pagina, cosi' filtri e scheda aperta non restano quelli del bot prima.
  return <CasellaContatti key={botId} botId={botId} />;
}

function CasellaContatti({ botId }) {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const selezionatoId = params.get("contatto");

  const [vista, setVista] = useState("open");
  const [pagina, setPagina] = useState(1);
  const [cerca, setCerca] = useState("");
  const [q, setQ] = useState("");
  const [daCancellare, setDaCancellare] = useState(null);

  useEffect(() => {
    const attesa = setTimeout(() => {
      setQ(cerca.trim());
      setPagina(1);
    }, ATTESA_RICERCA);
    return () => clearTimeout(attesa);
  }, [cerca]);

  const { data: bot } = useBot(botId);
  const { data: stats } = useContactStats(botId);
  const { data: formData } = useContactForm(botId);
  const { data: elenco, isLoading } = useContacts(botId, {
    page: pagina,
    limit: PER_PAGINA,
    status: vista,
    ...(q ? { q } : {}),
  });
  const contatti = elenco?.contacts || [];
  const nellElenco = contatti.find((c) => c.id === selezionatoId);
  // Aperto da un link (o finito su un'altra pagina dell'elenco): si chiede da solo.
  const singolo = useContact(botId, nellElenco ? null : selezionatoId);
  const selezionato = nellElenco || singolo.data || null;

  const cambiaStato = useSetContactStatus(botId);
  const cancella = useDeleteContact(botId);

  // Aprire un contatto vuol dire averlo letto: il pallino dei nuovi resta vero
  // senza doverci pensare.
  useEffect(() => {
    if (selezionato?.status === "new") {
      cambiaStato.mutate({ contactId: selezionato.id, status: "read" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selezionato?.id]);

  // Sul telefono la scheda prende il posto dell'elenco: si riparte dalla sua
  // cima, anche se l'elenco era scorso in basso.
  const scheda = useRef(null);
  useEffect(() => {
    if (selezionatoId && !schermoLargo()) scheda.current?.scrollIntoView?.({ block: "start" });
  }, [selezionatoId]);

  const scegli = (id) => {
    const prossimi = new URLSearchParams(params);
    if (id) prossimi.set("contatto", id);
    else prossimi.delete("contatto");
    // Dall'elenco alla scheda si aggiunge un passo alla cronologia: sul
    // telefono "indietro" torna all'elenco. Tra una scheda e l'altra no.
    setParams(prossimi, { replace: Boolean(selezionatoId && id) });
  };

  // Dopo archivia o cancella, su schermo largo si passa al contatto dopo,
  // come nella posta; sul telefono si torna all'elenco.
  const vicino = (id) => {
    const i = contatti.findIndex((c) => c.id === id);
    if (!schermoLargo() || i < 0) return null;
    return contatti[i + 1]?.id || contatti[i - 1]?.id || null;
  };

  // avanti: al contatto dopo; chiudi: di nuovo all'elenco.
  const imposta = async (contact, status, { avanti = false, chiudi = false } = {}) => {
    const dopo = avanti ? vicino(contact.id) : chiudi ? null : undefined;
    try {
      await cambiaStato.mutateAsync({ contactId: contact.id, status });
      if (dopo !== undefined) scegli(dopo);
    } catch (error) {
      toast({ title: "Modifica non riuscita", description: error?.message || "Riprova fra poco.", variant: "destructive" });
    }
  };

  const confermaCancellazione = async () => {
    const dopo = vicino(daCancellare.id);
    try {
      await cancella.mutateAsync(daCancellare.id);
      setDaCancellare(null);
      scegli(dopo);
      toast({ title: "Contatto cancellato" });
    } catch (error) {
      toast({ title: "Cancellazione non riuscita", description: error?.message || "Riprova fra poco.", variant: "destructive" });
    }
  };

  const esporta = async () => {
    try {
      const blob = await api.contacts.exportCsv(botId);
      const url = URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `contatti-${bot?.name || "bot"}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: "Esportazione non riuscita", description: error?.message || "Riprova fra poco.", variant: "destructive" });
    }
  };

  const cambiaVista = (nuova) => {
    setVista(nuova);
    setPagina(1);
  };

  const nessunContatto = stats && stats.total === 0;
  const moduloAcceso = Boolean(formData?.form?.enabled);

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      <IntestazioneCasella
        icona={Inbox}
        titolo="Contatti"
        dettagli={stats?.total ? <Conteggi stats={stats} /> : null}
        azioni={
          <>
            <Link to={`/bots/${botId}/integrazioni/modulo-contatti`} className={GRIGIO}>
              Configura il modulo
            </Link>
            {stats?.total ? (
              <button type="button" onClick={esporta} className={GRIGIO}>
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                Esporta CSV
              </button>
            ) : null}
          </>
        }
      />

      <div className="pu-fade-up mx-auto max-w-[1400px] pb-10">
        {nessunContatto ? (
          <Benvenuto botId={botId} moduloAcceso={moduloAcceso} />
        ) : (
          <>
            {/* Sul telefono, con un contatto aperto, conta solo la scheda. */}
            <div className={`${selezionatoId ? "hidden lg:flex" : "flex"} flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`}>
              <Interruttore
                etichetta="Quali contatti"
                valore={vista}
                onCambia={cambiaVista}
                voci={[
                  { valore: "open", etichetta: "In arrivo", numero: stats ? (stats.new || 0) + (stats.read || 0) : null },
                  { valore: "archived", etichetta: "Archiviati", numero: stats ? stats.archived || 0 : null },
                ]}
              />
              <Ricerca
                valore={cerca}
                onCambia={setCerca}
                segnaposto="Cerca nome, email, telefono o richiesta"
                etichetta="Cerca tra i contatti"
              />
            </div>

            <div className="mt-4 grid items-start gap-4 lg:grid-cols-[minmax(320px,400px)_minmax(0,1fr)]">
              {/* Sul telefono si vede una cosa alla volta: l'elenco o la scheda. */}
              <div className={selezionatoId ? "hidden lg:block" : ""}>
                <Elenco
                  contatti={contatti}
                  caricamento={isLoading}
                  selezionatoId={selezionatoId}
                  onScegli={scegli}
                  paginazione={elenco?.pagination}
                  onPagina={setPagina}
                  vuoto={
                    <ElencoVuoto
                      vista={vista}
                      q={q}
                      stats={stats}
                      onAzzeraRicerca={() => setCerca("")}
                    />
                  }
                />
              </div>
              <div ref={scheda} className={`scroll-mt-4 lg:sticky lg:top-4 ${selezionatoId ? "" : "hidden lg:block"}`}>
                {selezionato ? (
                  <Scheda
                    key={selezionato.id}
                    contact={selezionato}
                    botId={botId}
                    inCorso={cambiaStato.isPending}
                    onIndietro={() => scegli(null)}
                    onArchivia={() => imposta(selezionato, "archived", { avanti: true })}
                    onRipristina={() => imposta(selezionato, "new")}
                    // Aprire il contatto dopo lo segnerebbe come letto: si torna all'elenco.
                    onDaLeggere={() => imposta(selezionato, "new", { chiudi: true })}
                    onCancella={() => setDaCancellare(selezionato)}
                  />
                ) : selezionatoId && singolo.isLoading ? (
                  <BrandCard className="space-y-4 p-6">
                    <Skeleton className="h-10 w-2/3" />
                    <Skeleton className="h-24 w-full" />
                  </BrandCard>
                ) : selezionatoId ? (
                  <BrandCard className="p-6 text-sm text-[#64748B]">
                    Questo contatto non c'è più: forse è stato cancellato.{" "}
                    <button type="button" className="font-medium text-[#18181B] underline" onClick={() => scegli(null)}>
                      Torna all'elenco
                    </button>
                  </BrandCard>
                ) : (
                  <NessunaScelta testo="Scegli un contatto dall'elenco" />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={Boolean(daCancellare)} onOpenChange={(open) => !open && setDaCancellare(null)}>
        <DialogContent className="max-w-md rounded-[14px]">
          <DialogHeader>
            <DialogTitle>Cancellare questo contatto?</DialogTitle>
            <DialogDescription>
              I dati di {daCancellare?.headline} vengono rimossi per sempre, file compresi. È quello che va fatto
              quando una persona chiede di cancellare i suoi dati.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button type="button" className={GRIGIO} onClick={() => setDaCancellare(null)}>
              Annulla
            </button>
            <button
              type="button"
              className={`${BOTTONE} bg-[#DC2626] text-white hover:bg-[#B91C1C]`}
              onClick={confermaCancellazione}
              disabled={cancella.isPending}
            >
              {cancella.isPending ? "Cancellazione…" : "Cancella per sempre"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Sotto il titolo: quanti da leggere, in arrivo e archiviati. */
function Conteggi({ stats }) {
  const inArrivo = (stats.new || 0) + (stats.read || 0);
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#475569]">
      {stats.new ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-[#A855F7]" aria-hidden="true" />
          <strong className="font-semibold text-[#020617]">{stats.new}</strong> da leggere
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <Check className="h-3.5 w-3.5 text-[#0F7A3D]" aria-hidden="true" />
          Tutti letti
        </span>
      )}
      <span className="text-[#CBD5E1]" aria-hidden="true">·</span>
      <span>
        <strong className="font-semibold text-[#020617]">{inArrivo}</strong> in arrivo
      </span>
      {stats.archived ? (
        <>
          <span className="text-[#CBD5E1]" aria-hidden="true">·</span>
          <span>
            <strong className="font-semibold text-[#020617]">{stats.archived}</strong>{" "}
            {stats.archived === 1 ? "archiviato" : "archiviati"}
          </span>
        </>
      ) : null}
    </p>
  );
}

/** Il tondo con le iniziali. */
function Iniziali({ contact, grande = false }) {
  return (
    <span className="relative shrink-0">
      <span
        className={`flex items-center justify-center rounded-full bg-[#F1F5F9] font-semibold text-[#334155] ${
          grande ? "h-12 w-12 text-base" : "h-9 w-9 text-xs"
        }`}
        aria-hidden="true"
      >
        {iniziali(contact)}
      </span>
    </span>
  );
}

/** La riga sotto il nome: di cosa si parlava, o in mancanza un recapito. */
function anteprima(contact) {
  return (
    contact.context?.summary ||
    (contact.context?.topic ? `«${contact.context.topic}»` : "") ||
    emailDi(contact) ||
    telefonoDi(contact) ||
    "Nessun riepilogo della conversazione"
  );
}

function Elenco({ contatti, caricamento, selezionatoId, onScegli, paginazione, onPagina, vuoto }) {
  const lista = useRef(null);

  // Frecce su e giu' per passare da un contatto all'altro.
  const muovi = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const i = contatti.findIndex((c) => c.id === selezionatoId);
    const prossimo = contatti[e.key === "ArrowDown" ? i + 1 : Math.max(0, i - 1)];
    if (!prossimo) return;
    e.preventDefault();
    onScegli(prossimo.id);
    lista.current?.querySelector(`[data-contatto="${prossimo.id}"]`)?.focus();
  };

  if (caricamento && !contatti.length) {
    return (
      <BrandCard className="space-y-1 p-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-start gap-3 p-3">
            <Skeleton className="h-9 w-9 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-1/2" />
              <Skeleton className="h-3 w-full" />
            </div>
          </div>
        ))}
      </BrandCard>
    );
  }
  if (!contatti.length) return <BrandCard>{vuoto}</BrandCard>;

  // Raggruppati per giorno, nell'ordine in cui arrivano (dal piu' recente).
  const gruppi = raggruppa(contatti, (contact) => gruppoDi(contact.created_at));

  return (
    <BrandCard className="overflow-hidden">
      <div ref={lista} onKeyDown={muovi}>
        {gruppi.map((gruppo) => (
          <section key={gruppo.nome} aria-label={gruppo.nome}>
            <h2 className="px-4 pb-1 pt-3.5 text-xs font-medium text-[#94A3B8]">{gruppo.nome}</h2>
            <ul className="px-1.5 pb-1.5">
              {gruppo.elementi.map((contact) => (
                <li key={contact.id}>
                  <VoceElenco
                    contact={contact}
                    selezionato={contact.id === selezionatoId}
                    onScegli={() => onScegli(contact.id)}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <Paginazione paginazione={paginazione} perPagina={PER_PAGINA} onPagina={onPagina} cosa="Contatti" />
    </BrandCard>
  );
}

function VoceElenco({ contact, selezionato, onScegli, esempio = false }) {
  const nuovo = contact.status === "new";
  return (
    <button
      type="button"
      data-contatto={contact.id}
      onClick={onScegli}
      tabIndex={esempio ? -1 : undefined}
      aria-current={selezionato ? "true" : undefined}
      className={`flex w-full items-start gap-3 rounded-[10px] px-2.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
        selezionato ? "bg-[#F1F5F9]" : "hover:bg-[#F8FAFC]"
      }`}
    >
      <Iniziali contact={contact} />
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-sm leading-5 text-[#020617] ${nuovo ? "font-semibold" : "font-medium"}`}>
          {contact.headline}
          {nuovo ? <span className="sr-only"> (da leggere)</span> : null}
        </span>
        <span className={`mt-0.5 line-clamp-2 text-[13px] leading-5 ${nuovo ? "text-[#334155]" : "text-[#64748B]"}`}>
          {anteprima(contact)}
        </span>
      </span>
      {/* L'ora o il giorno e, sotto, il pallino viola se e' da leggere: una
          colonna a parte, cosi' il pallino non allunga la riga del nome. */}
      <span className="flex shrink-0 flex-col items-end gap-1.5">
        <span className={`text-xs leading-5 tabular-nums ${nuovo ? "font-semibold text-[#020617]" : "text-[#94A3B8]"}`}>
          {quandoBreve(contact.created_at)}
        </span>
        {nuovo ? <span className="h-2 w-2 rounded-full bg-[#A855F7]" aria-hidden="true" /> : null}
      </span>
    </button>
  );
}

function ElencoVuoto({ vista, q, stats, onAzzeraRicerca }) {
  let testo;
  if (q) testo = `Nessun contatto per «${q}».`;
  else if (vista === "archived") testo = "Nessun contatto archiviato. Archivia quelli già gestiti: restano qui e nell'esportazione.";
  else if (stats?.archived) testo = "Niente in arrivo: li hai gestiti tutti.";
  else testo = "Ancora nessun contatto.";
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <p className="max-w-xs text-sm text-[#64748B]">{testo}</p>
      {q ? (
        <button type="button" className={GRIGIO} onClick={onAzzeraRicerca}>
          Cancella la ricerca
        </button>
      ) : null}
    </div>
  );
}

/** Nessun contatto ancora: cosa arrivera' qui, e da dove si parte. */
function Benvenuto({ botId, moduloAcceso }) {
  const esempio = {
    id: "esempio",
    headline: "Giulia Bianchi",
    status: "new",
    created_at: new Date().toISOString(),
    context: { summary: "Vorrebbe un preventivo per rifare il bagno, circa 6 mq, entro la primavera." },
  };
  return (
    <BrandCard className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-center">
      <div className="max-w-lg space-y-3">
        <h2 className="text-lg font-semibold text-[#020617]">Qui arrivano le persone che vogliono essere ricontattate</h2>
        <p className="text-sm leading-6 text-[#475569]">
          Quando il bot propone il modulo in chat e qualcuno lo compila, lo trovi qui con il riassunto della
          conversazione e i pulsanti per scrivergli o chiamarlo. Ti avvisiamo anche per email.
        </p>
        <p className="text-sm leading-6 text-[#475569]">
          {moduloAcceso ? "Il modulo è attivo: manca solo il primo contatto." : "Il modulo è spento: accendilo e il bot lo proporrà quando serve."}
        </p>
        <Link to={`/bots/${botId}/integrazioni/modulo-contatti`} className={`${moduloAcceso ? GRIGIO : SCURO} mt-2`}>
          {moduloAcceso ? "Vedi il modulo" : "Attiva il modulo"}
        </Link>
      </div>
      <div aria-hidden="true">
        <HandNote className="mb-1">così ti arriva un contatto</HandNote>
        <div className="pointer-events-none rounded-[14px] border border-[#E2E8F0] bg-white p-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
          <VoceElenco contact={esempio} selezionato={false} onScegli={() => {}} esempio />
        </div>
      </div>
    </BrandCard>
  );
}

// ---------------------------------------------------------------------------
// La scheda del contatto
// ---------------------------------------------------------------------------

function Scheda({ contact, botId, inCorso, onIndietro, onArchivia, onRipristina, onDaLeggere, onCancella }) {
  const archiviato = contact.status === "archived";
  const valori = contact.values || [];

  return (
    <BrandCard className="pu-carta-in overflow-hidden">
      {/* La testata in una fascia, come la conversazione: chi e' e quando, a
          destra come ricontattarlo (sotto, se non ci sta). */}
      <div className="space-y-3 px-5 py-4">
        <button
          type="button"
          onClick={onIndietro}
          className="-ml-1 inline-flex items-center gap-1.5 rounded-md px-1 text-sm font-medium text-[#64748B] hover:text-[#020617] lg:hidden"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Tutti i contatti
        </button>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Iniziali contact={contact} />
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <h2 className="min-w-0 truncate text-base font-semibold leading-6 text-[#020617]">{contact.headline}</h2>
                {archiviato ? (
                  <span className="inline-flex shrink-0 items-center rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-medium text-[#475569]">
                    Archiviato
                  </span>
                ) : null}
              </div>
              <p className="mt-0.5 text-xs leading-5 text-[#64748B]">{quandoEsteso(contact.created_at)}</p>
            </div>
          </div>
          <AzioniRapide contact={contact} />
        </div>
      </div>

      <div className="space-y-6 border-t border-[#E2E8F0] p-5 sm:p-6">
        {contact.context?.summary || contact.context?.topic ? (
          <Blocco titolo="Cosa cercava">
            <div className="rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] p-4">
              {contact.context?.summary ? (
                <p className="text-sm leading-6 text-[#0F172A]">{contact.context.summary}</p>
              ) : null}
              {contact.context?.topic ? (
                <p className={`text-xs leading-5 text-[#64748B] ${contact.context?.summary ? "mt-2" : ""}`}>
                  Ha iniziato chiedendo: «{contact.context.topic}»
                </p>
              ) : null}
            </div>
          </Blocco>
        ) : null}

        {valori.length ? (
          <Blocco titolo="Cosa ha scritto nel modulo">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-[minmax(120px,180px)_minmax(0,1fr)]">
              {valori.map((v) => (
                <React.Fragment key={v.field_id}>
                  <dt className="text-xs font-medium leading-5 text-[#64748B] sm:pt-px">{v.label}</dt>
                  <dd className="min-w-0 text-sm leading-5 text-[#0F172A]">
                    <Valore valore={v.value} botId={botId} contactId={contact.id} />
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          </Blocco>
        ) : null}

        {contact.consents?.length ? (
          <Blocco titolo="Consensi">
            <ul className="space-y-2">
              {contact.consents.map((c) => (
                <li key={c.field_id} className="flex gap-2 text-sm leading-5 text-[#0F172A]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0F7A3D]" aria-hidden="true" />
                  <span className="min-w-0">
                    {c.text}
                    {c.link ? (
                      <a
                        href={c.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-1 inline-flex translate-y-0.5 text-[#64748B] hover:text-[#020617]"
                        aria-label="Apri il testo completo"
                      >
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                      </a>
                    ) : null}
                    {c.accepted_at ? (
                      <span className="block text-xs text-[#94A3B8]">Accettato il {quandoEsteso(c.accepted_at)}</span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </Blocco>
        ) : null}

        <Blocco titolo="Da dove arriva">
          <ul className="space-y-2 text-sm leading-5 text-[#0F172A]">
            <li className="text-[#475569]">
              {contact.source === "tool"
                ? "Il bot gli ha proposto il modulo durante la conversazione."
                : "Ha chiesto lui di essere ricontattato, con il pulsante in chat."}
            </li>
            {contact.context?.page_url ? (
              <li>
                <a
                  href={contact.context.page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex max-w-full items-center gap-1.5 font-medium text-[#18181B] hover:underline"
                >
                  <span className="truncate">{paginaLeggibile(contact.context)}</span>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[#94A3B8]" aria-hidden="true" />
                </a>
              </li>
            ) : null}
            {contact.session_id ? (
              <li>
                <Link
                  to={`/bots/${botId}/conversations?sessione=${encodeURIComponent(contact.session_id)}`}
                  className="inline-flex items-center gap-1.5 font-medium text-[#18181B] hover:underline"
                >
                  <MessageSquare className="h-4 w-4 text-[#94A3B8]" aria-hidden="true" />
                  Leggi la conversazione
                </Link>
              </li>
            ) : null}
          </ul>
        </Blocco>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#E2E8F0] px-5 py-3 sm:px-6">
        <button
          type="button"
          onClick={onCancella}
          className={`${BOTTONE} -ml-2 text-[#B91C1C] hover:bg-red-50`}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          Cancella
        </button>
        <div className="flex flex-wrap gap-2">
          {archiviato ? (
            <button type="button" className={GRIGIO} onClick={onRipristina} disabled={inCorso}>
              <ArchiveRestore className="h-3.5 w-3.5" aria-hidden="true" />
              Riporta in arrivo
            </button>
          ) : (
            <>
              <button type="button" className={`${BOTTONE} text-[#334155] hover:bg-[#F1F5F9]`} onClick={onDaLeggere} disabled={inCorso}>
                <CircleDot className="h-3.5 w-3.5" aria-hidden="true" />
                Segna da leggere
              </button>
              <button type="button" className={GRIGIO} onClick={onArchivia} disabled={inCorso}>
                <Archive className="h-3.5 w-3.5" aria-hidden="true" />
                Archivia
              </button>
            </>
          )}
        </div>
      </div>
    </BrandCard>
  );
}

function Blocco({ titolo, children }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-medium text-[#64748B]">{titolo}</h3>
      {children}
    </section>
  );
}

/**
 * Scrivere, chiamare, WhatsApp: solo quelli che si possono fare con i
 * recapiti lasciati. Il primo e' l'azione principale.
 */
function AzioniRapide({ contact }) {
  const email = emailDi(contact);
  const telefono = telefonoDi(contact);
  const whatsapp = whatsappDa(telefono);
  const nome = nomeDi(contact).split(/\s+/)[0];
  const saluto = nome ? `Ciao ${nome},` : "Ciao,";

  const azioni = [];
  if (email) {
    azioni.push({
      chiave: "email",
      etichetta: "Scrivi un'email",
      icona: <Mail className="h-3.5 w-3.5" aria-hidden="true" />,
      href: `mailto:${email}?subject=${encodeURIComponent("La tua richiesta")}&body=${encodeURIComponent(`${saluto}\n\n`)}`,
    });
  }
  if (telefono) {
    azioni.push({
      chiave: "telefono",
      etichetta: "Chiama",
      icona: <Phone className="h-3.5 w-3.5" aria-hidden="true" />,
      href: telLink(telefono),
    });
  }
  if (whatsapp) {
    azioni.push({
      chiave: "whatsapp",
      etichetta: "WhatsApp",
      icona: (
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="#25D366" aria-hidden="true">
          <path d={siWhatsapp.path} />
        </svg>
      ),
      href: `https://wa.me/${whatsapp}?text=${encodeURIComponent(`${saluto} ti scrivo per la richiesta che ci hai lasciato in chat.`)}`,
      esterno: true,
    });
  }

  if (!azioni.length) {
    return (
      <p className="text-xs text-[#64748B]">
        Non ha lasciato né email né telefono: trovi quello che ha scritto qui sotto.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {azioni.map((azione, i) => (
        <a
          key={azione.chiave}
          href={azione.href}
          {...(azione.esterno ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className={i === 0 ? SCURO : GRIGIO}
        >
          {i === 0 && azione.chiave === "whatsapp" ? (
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
              <path d={siWhatsapp.path} />
            </svg>
          ) : (
            azione.icona
          )}
          {azione.etichetta}
        </a>
      ))}
    </div>
  );
}

/** Un valore del modulo: email e telefono cliccabili e copiabili, scelte come etichette, file da aprire. */
function Valore({ valore, botId, contactId }) {
  if (isFile(valore)) return <ValoreFile botId={botId} contactId={contactId} value={valore} />;
  if (Array.isArray(valore)) {
    return (
      <span className="flex flex-wrap gap-1.5">
        {valore.map((voce) => (
          <span key={voce} className="rounded-full bg-[#F1F5F9] px-2 py-0.5 text-xs font-medium text-[#334155]">
            {voce}
          </span>
        ))}
      </span>
    );
  }
  const testo = String(valore ?? "");
  if (isEmail(testo)) {
    return (
      <span className="flex min-w-0 items-center gap-1">
        <a href={`mailto:${testo.trim()}`} className="truncate font-medium text-[#18181B] hover:underline">
          {testo}
        </a>
        <Copia testo={testo.trim()} cosa="l'email" />
      </span>
    );
  }
  if (isTelefono(testo)) {
    return (
      <span className="flex min-w-0 items-center gap-1">
        <a href={telLink(testo)} className="font-medium text-[#18181B] hover:underline">
          {testo}
        </a>
        <Copia testo={testo.trim()} cosa="il numero" />
      </span>
    );
  }
  return <span className="whitespace-pre-line break-words">{testo}</span>;
}

/** Copia negli appunti; la spunta per un attimo dice che e' fatto. */
function Copia({ testo, cosa }) {
  const [fatto, setFatto] = useState(false);
  const copia = async () => {
    try {
      await navigator.clipboard.writeText(testo);
      setFatto(true);
      setTimeout(() => setFatto(false), 1500);
    } catch {
      // Senza permesso sugli appunti resta il testo da selezionare a mano.
    }
  };
  return (
    <button
      type="button"
      onClick={copia}
      aria-label={fatto ? "Copiato" : `Copia ${cosa}`}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#334155]"
    >
      {fatto ? <Check className="h-3.5 w-3.5 text-[#0F7A3D]" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
    </button>
  );
}

/**
 * Un file allegato dal visitatore. Il container e' privato: il link si chiede
 * al momento del clic e vale pochi minuti.
 */
function ValoreFile({ botId, contactId, value }) {
  const { toast } = useToast();
  const [inApertura, setInApertura] = useState(false);

  const apri = async () => {
    // La finestra si apre dentro il clic: dopo l'attesa della risposta il
    // browser la bloccherebbe come popup.
    const finestra = window.open("", "_blank");
    setInApertura(true);
    try {
      const { url } = await api.contacts.fileUrl(botId, contactId, value.upload_id);
      if (finestra) {
        finestra.opener = null;
        finestra.location.href = url;
      } else {
        toast({
          title: "Il browser ha bloccato la finestra",
          description: "Consenti i popup per questa pagina e riprova.",
          variant: "destructive",
        });
      }
    } catch (error) {
      finestra?.close();
      toast({ title: "File non disponibile", description: error?.message || "Riprova fra poco.", variant: "destructive" });
    } finally {
      setInApertura(false);
    }
  };

  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <Paperclip className="h-3.5 w-3.5 shrink-0 text-[#94A3B8]" aria-hidden="true" />
        <span className="break-all">{value.name}</span>
        <span className="shrink-0 text-xs text-[#94A3B8]">{formatBytes(value.size)}</span>
      </span>
      <button type="button" className={`${GRIGIO} h-7 px-2.5`} onClick={apri} disabled={inApertura}>
        {inApertura ? "Apertura…" : "Apri"}
      </button>
    </span>
  );
}
